import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Download, Mail, Printer } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Money } from '../../components/Money';
import { Skeleton } from '../../components/Skeleton';
import { useInvoice } from '../../api/hooks';
import { formatDate } from '../../lib/format';
import { useBrand } from '../../brand/BrandProvider';

const STATUS_TONE: Record<string, 'success' | 'warning' | 'error' | 'neutral'> = {
  paid: 'success',
  open: 'warning',
  partial_refunded: 'warning',
  refunded: 'neutral',
  uncollectible: 'error',
  void: 'neutral',
  draft: 'neutral',
};

const PROVIDER_LABEL: Record<string, string> = {
  sslcommerz: 'SSLCommerz',
  manual_cash: 'Cash',
  bkash: 'bKash',
  nagad: 'Nagad',
  bank_transfer: 'Bank transfer',
  stripe: 'Stripe',
  paypal: 'PayPal',
};

/** Invoice detail page — §5.14. Loads the invoice from the route id with its real lines. */
export function InvoiceDetail() {
  const brand = useBrand();
  const { id } = useParams({ strict: false }) as { id?: string };
  const { data: invoice, isLoading, isError } = useInvoice(id ?? '');

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto space-y-4" aria-busy="true">
        <Skeleton className="h-6 w-40 rounded" />
        <Skeleton className="h-96 rounded-lg" />
      </div>
    );
  }

  if (isError || !invoice) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
        <Link
          to="/billing"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
        >
          <ArrowLeft className="w-4 h-4" /> Back to billing
        </Link>
        <Card className="mt-5 p-10 text-center text-sm text-zinc-500 dark:text-zinc-400">
          This invoice doesn’t exist or you don’t have access to it.
        </Card>
      </div>
    );
  }

  const settledPayment = invoice.payments.find((p) => p.status === 'succeeded');

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <div className="no-print mb-5 flex items-center justify-between gap-4 flex-wrap">
        <Link
          to="/billing"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
        >
          <ArrowLeft className="w-4 h-4" /> Back to billing
        </Link>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<Printer className="w-4 h-4" />}
            onClick={() => window.print()}
          >
            <span className="hidden sm:inline">Print</span>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<Mail className="w-4 h-4" />}
            onClick={() => { window.location.href = `mailto:?subject=${encodeURIComponent(`Invoice ${invoice.number}`)}&body=${encodeURIComponent(`Invoice ${invoice.number} — please find it at ${window.location.href}`)}`; }}
          >
            <span className="hidden sm:inline">Email me a copy</span>
          </Button>
          <Button
            size="sm"
            iconLeft={<Download className="w-4 h-4" />}
            onClick={() => window.print()}
          >
            <span className="hidden sm:inline">Save as PDF</span>
            <span className="sm:hidden">PDF</span>
          </Button>
        </div>
      </div>

      <Card className="print-document overflow-hidden">
        {/* Header */}
        <div className="px-6 sm:px-10 pt-8 sm:pt-10 pb-6 border-b border-zinc-200 dark:border-zinc-700">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                Invoice
              </p>
              <h1 className="mt-1 mono text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 tabular-nums">
                {invoice.number}
              </h1>
              <div className="mt-2 flex items-center gap-3 text-xs text-zinc-500 dark:text-zinc-400 flex-wrap">
                <span>
                  Issued{' '}
                  <span className="text-zinc-700 dark:text-zinc-200 tabular-nums font-medium">
                    {invoice.issuedAt ? formatDate(invoice.issuedAt) : '—'}
                  </span>
                </span>
                <span aria-hidden="true">·</span>
                <span>
                  Due{' '}
                  <span className="text-zinc-700 dark:text-zinc-200 tabular-nums font-medium">
                    {invoice.dueAt ? formatDate(invoice.dueAt) : '—'}
                  </span>
                </span>
              </div>
            </div>
            <Badge tone={STATUS_TONE[invoice.status] ?? 'neutral'}>
              {invoice.status.replace(/_/g, ' ')}
            </Badge>
          </div>
        </div>

        {/* From / To */}
        <div className="px-6 sm:px-10 py-6 grid sm:grid-cols-2 gap-6 border-b border-zinc-200 dark:border-zinc-700">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
              From
            </p>
            <div className="text-sm text-zinc-900 dark:text-zinc-100 font-medium">
              {brand.name}
            </div>
            <p className="mt-0.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">
              {brand.supportEmail}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
              Bill to
            </p>
            <div className="text-sm text-zinc-900 dark:text-zinc-100 font-medium">
              {invoice.business?.name ?? 'Your business'}
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="px-2 sm:px-6">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              <tr className="border-b border-zinc-200 dark:border-zinc-700">
                <th className="text-left font-medium px-4 sm:px-4 py-3">Item</th>
                <th className="text-right font-medium px-4 py-3 tabular-nums w-16">Qty</th>
                <th className="text-right font-medium px-4 py-3 tabular-nums w-32">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-900">
              {invoice.lines.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-xs text-zinc-500 dark:text-zinc-400">
                    No line items on this invoice.
                  </td>
                </tr>
              ) : (
                invoice.lines.map((line) => (
                  <tr key={line.id}>
                    <td className="px-4 py-4">
                      <div className="font-medium text-zinc-900 dark:text-zinc-100">{line.description}</div>
                      <div className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400 capitalize">
                        {line.lineType.replace(/_/g, ' ')}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right tabular-nums text-zinc-700 dark:text-zinc-200">
                      {line.quantity}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Money minor={line.amountMinor} hideCents tone="neutral" className="text-zinc-900 dark:text-zinc-100" />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="px-4 sm:px-6 py-6 border-t border-zinc-200 dark:border-zinc-700 flex justify-end">
          <dl className="w-full sm:w-72 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-zinc-600 dark:text-zinc-300">Subtotal</dt>
              <dd className="tabular-nums text-zinc-900 dark:text-zinc-100">
                <Money minor={invoice.subtotalMinor} hideCents tone="neutral" />
              </dd>
            </div>
            {invoice.discountMinor > 0 && (
              <div className="flex items-center justify-between">
                <dt className="text-zinc-600 dark:text-zinc-300">Discount</dt>
                <dd className="tabular-nums text-emerald-700 dark:text-emerald-300">
                  −<Money minor={invoice.discountMinor} hideCents tone="neutral" />
                </dd>
              </div>
            )}
            <div className="flex items-center justify-between">
              <dt className="text-zinc-600 dark:text-zinc-300">Tax</dt>
              <dd className="tabular-nums text-zinc-900 dark:text-zinc-100">
                <Money minor={invoice.taxMinor} hideCents tone="neutral" />
              </dd>
            </div>
            <div className="flex items-center justify-between border-t border-zinc-200 dark:border-zinc-700 pt-2 mt-2">
              <dt className="font-semibold text-zinc-950 dark:text-zinc-50">Total</dt>
              <dd className="tabular-nums font-semibold text-lg text-zinc-950 dark:text-zinc-50">
                <Money minor={invoice.totalMinor} hideCents tone="neutral" />
              </dd>
            </div>
            {invoice.amountPaidMinor > 0 && invoice.amountPaidMinor < invoice.totalMinor && (
              <div className="flex items-center justify-between">
                <dt className="text-zinc-600 dark:text-zinc-300">Paid so far</dt>
                <dd className="tabular-nums text-zinc-900 dark:text-zinc-100">
                  <Money minor={invoice.amountPaidMinor} hideCents tone="neutral" />
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Payment status */}
        <div className="px-6 sm:px-10 py-5 bg-zinc-50 dark:bg-zinc-800/40 border-t border-zinc-200 dark:border-zinc-700 flex items-center gap-3 flex-wrap">
          <Badge tone={STATUS_TONE[invoice.status] ?? 'neutral'}>
            {invoice.status.replace(/_/g, ' ')}
          </Badge>
          {settledPayment ? (
            <span className="text-sm text-zinc-700 dark:text-zinc-200">
              via{' '}
              <span className="font-medium text-zinc-900 dark:text-zinc-100">
                {PROVIDER_LABEL[settledPayment.provider] ?? settledPayment.provider}
              </span>
              {settledPayment.paidAt && (
                <>
                  {' '}on <span className="tabular-nums">{formatDate(settledPayment.paidAt)}</span>
                </>
              )}
            </span>
          ) : invoice.status === 'open' ? (
            <span className="text-sm text-zinc-700 dark:text-zinc-200">
              Awaiting payment — pay from the <Link to="/billing" className="text-brand dark:text-brand-dm hover:underline">Billing</Link> page.
            </span>
          ) : null}
        </div>
      </Card>

      <p className="mt-6 text-xs text-zinc-500 dark:text-zinc-400 text-center">
        Reach out to{' '}
        <a href={`mailto:${brand.supportEmail}`} className="text-brand dark:text-brand-dm hover:underline">
          {brand.supportEmail}
        </a>{' '}
        if anything looks off — we keep records for 7 years.
      </p>
    </div>
  );
}
